import React, { useState } from 'react';
import axios from 'axios';
import { LockedEmailCheckbox } from '../common/LockedEmailChannel';

const SCHEDULE_TYPES = [
  { id: 'once', label: 'One-time' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'biweekly', label: 'Every 2 weeks' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'custom', label: 'Custom interval' },
];

const ReminderEditor = ({ savedItemId, reminder, onClose, onSaved }) => {
  const isEdit = !!reminder?.id;
  const [scheduleType, setScheduleType] = useState(reminder?.schedule_type || 'once');
  const [date, setDate] = useState(() => {
    const at = reminder?.schedule?.at;
    if (at) return String(at).slice(0, 10);
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [time, setTime] = useState(() => {
    const at = reminder?.schedule?.at;
    if (at && String(at).includes('T')) return String(at).slice(11, 16);
    return '09:00';
  });
  const [note, setNote] = useState(reminder?.note || '');
  const [calendar, setCalendar] = useState(reminder?.channels?.calendar === true);
  const [intervalDays, setIntervalDays] = useState(
    String(reminder?.schedule?.interval_days || 7)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const buildSchedule = () => {
    const sched = {
      at: `${date}T${time}:00`,
      timezone: userTimezone,
    };
    if (scheduleType === 'custom') {
      sched.interval_days = Math.max(1, parseInt(intervalDays, 10) || 7);
    }
    return sched;
  };

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const body = {
        schedule_type: scheduleType,
        schedule: buildSchedule(),
        channels: { in_app: true, calendar },
        note: note.trim() || undefined,
      };
      if (isEdit) {
        const res = await axios.put(`/api/engagement/reminders/${reminder.id}`, body);
        onSaved?.(res.data.reminder);
      } else {
        const res = await axios.post(`/api/engagement/saved/${savedItemId}/reminders`, body);
        onSaved?.(res.data.reminder);
      }
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save reminder');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/40">
      <div className="surface-panel p-6 max-w-md w-full light-surface">
        <h3 className="font-display text-h2 text-primary mb-2">
          {isEdit ? 'Edit reminder' : 'Set your reminder'}
        </h3>
        <p className="text-label text-secondary mb-4">
          You choose the schedule — Aura will not change or create reminders for you.
        </p>

        <label className="block text-label text-secondary mb-1">Schedule</label>
        <select
          value={scheduleType}
          onChange={(e) => setScheduleType(e.target.value)}
          className="w-full mb-3 input-editorial text-label"
        >
          {SCHEDULE_TYPES.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>

        <div className="flex gap-2 mb-3">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="flex-1 input-editorial"
          />
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-28 input-editorial"
          />
        </div>
        <p className="text-label text-faint mb-3 -mt-1">
          Times use your timezone ({userTimezone}).
        </p>

        {scheduleType === 'custom' && (
          <div className="mb-3">
            <label className="block text-label text-secondary mb-1">Repeat every (days)</label>
            <input
              type="number"
              min={1}
              max={365}
              value={intervalDays}
              onChange={(e) => setIntervalDays(e.target.value)}
              className="w-full input-editorial figure"
            />
          </div>
        )}

        <label className="block text-label text-secondary mb-1">Note (optional)</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Review this step and transfer $200"
          className="w-full mb-3 input-editorial resize-none"
          rows={2}
        />

        <label className="flex items-center gap-2 text-body text-secondary mb-2">
          <input
            type="checkbox"
            checked={calendar}
            onChange={(e) => setCalendar(e.target.checked)}
            className="rounded border-hairline text-pine focus:ring-pine"
          />
          Add to calendar (.ics feed)
        </label>

        <LockedEmailCheckbox
          id="reminder-channel-email"
          label="Email reminder"
          checked={reminder?.channels?.email === true}
          className="mb-4"
        />

        {error && <p className="text-danger text-body mb-3">{error}</p>}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="btn-primary flex-1 py-2 text-label disabled:opacity-50"
          >
            {saving ? 'Saving…' : isEdit ? 'Update' : 'Create reminder'}
          </button>
          <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 text-label">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReminderEditor;
